import {Auth} from "types/auth"
import {useMemo, useState} from "react"
import {fieldsService} from "services"
import {handleError} from "helpers"
import {Settings} from "types/settings"
import styles from "uiKit/StudentInfoPanel/StudentInfoPanel.module.css"
import {BaseInput, BaseTextArea} from "components/inputs"
import cx from "classnames"
import CircularSpin from "components/CircularSpin"
import {Icon} from "components/Icon"
import {Tooltip} from "antd"
import {useMutation} from "@tanstack/react-query"
import {BaseDatePicker} from "components/DateTimePicker"
import moment from "moment"
import {useModel} from "hooks"

type Props = {
    field: Settings.CustomField
    defaultValue: string | number
    student?: Auth.DepartmentStudent
    readOnly?: boolean
}

const CustomFieldContent = ({field, defaultValue, student, readOnly = false}: Props) => {
    const [isEditing, setEditing] = useState(false)
    const [value, setValue] = useState(defaultValue)
    const model = useModel()
    const editable = !readOnly
    const userDateFormat = model.getUserDateFormat()

    const displayValue = useMemo(() => {
        if (field.fieldType === Settings.CustomFieldType.Date) {
            return value ? moment.utc(value).format(userDateFormat) : "-"
        }
        return value || "-"
    }, [field.fieldType, value, userDateFormat])

    const updateCustomFieldsForProfileMutation = useMutation(
        (data: Settings.UpdateCustomFieldsPayload) => fieldsService.updateCustomFieldsForProfile(data),
        {
            onSuccess: () => setEditing(false),
            onError: (error) => handleError(error)
        }
    )

    const handleEditClick = () => {
        setEditing(true)
    }

    const renderInput = () => {
        switch (field.fieldType) {
            case Settings.CustomFieldType.Text:
                return <BaseInput value={value} onChange={setValue} className={styles.contentInput} />
            case Settings.CustomFieldType.Number:
                return <BaseInput value={value} type="number" onChange={setValue} className={styles.contentInput} />
            case Settings.CustomFieldType.Paragraph:
                return <BaseTextArea value={value} onChange={setValue} className={styles.contentInput} />
            case Settings.CustomFieldType.Date:
                return (
                    <BaseDatePicker
                        value={value}
                        dateOnly
                        onChange={setValue}
                        className={styles.contentInput}
                        suffixIcon={null}
                        clearIcon={null}
                    />
                )
            default:
                return null
        }
    }

    const handleSaveClick = () => {
        updateCustomFieldsForProfileMutation.mutate({
            customFields: [
                {
                    customFieldId: field.customFieldId,
                    value: value || null,
                    userId: student?.userId,
                    profileId: student?.profileId
                }
            ]
        })
    }

    return (
        <div className={styles.contentWrap}>
            <div
                className={cx(styles.contentTitle, {[styles.editable]: editable})}
                onClick={editable ? handleEditClick : undefined}
                title={field.label}>
                {field.label}
            </div>
            {isEditing ? (
                <div className={styles.contentValueWrap}>
                    <div className={cx(styles.contentValue)}>{renderInput()}</div>
                    <div className={styles.contentActions}>
                        {updateCustomFieldsForProfileMutation.isLoading ? (
                            <CircularSpin size="small" />
                        ) : (
                            <div className={styles.contentAction} onClick={handleSaveClick}>
                                <Icon icon="CHECK_CIRCLE" />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <Tooltip title={displayValue} placement="topRight">
                    <div
                        className={cx(styles.contentValue, {[styles.editable]: editable})}
                        onClick={editable ? handleEditClick : undefined}>
                        <span className={styles.displayValue}>{displayValue}</span>
                    </div>
                </Tooltip>
            )}
        </div>
    )
}

export default CustomFieldContent
